<template>
  <div class="mesh-cameras mesh-cameras_in-dialog">
    <div class="mesh-cameras__navigate">
      <div v-if="!hasCameraRestriction" class="tabs-container">
        <a
          :class="{'tabs-container__tab-item--active': selectedTab === tabs.MY}"
          class="tabs-container__tab-item"
          @click="selectTab(tabs.MY)"
        >
          {{ $t('myCameras') }}
        </a>
        <a
          :class="{'tabs-container__tab-item--active': selectedTab === tabs.FAV}"
          class="tabs-container__tab-item"
          @click="selectTab(tabs.FAV)"
        >
          {{ $t('favorites') }}
        </a>
        <a
          v-if="queryTabSearch"
          :class="{'tabs-container__tab-item--active': selectedTab === tabs.SEARCH}"
          class="tabs-container__tab-item"
          @click="selectTab(tabs.SEARCH)"
        >
          {{ $t('search') }}
        </a>
      </div>

      <SmartInputText
        v-if="hasCameraRestriction"
        v-model="restrictedSearch"
        :placeholder="$t('search')"
        @input="selectedPage = 1"
      />
      <CamerasFinder
        v-else
        v-model="querySearchCameras"
        :allowed-cameras="restrictedCamerasInfo"
        :allowed-camera-numbers="allowedCameraNumbers"
        :selectable-only-mode="true"
        @input="saveState()"
        @select-camera="selectCamera"
        @show-all-results="showAllSearchResults()"
      />
    </div>

    <div class="mesh-cameras__view-options">
      <SelectSort
        v-model="selectedSort"
        :available-params-sort="availableParamsSort"
        class="mesh-cameras__sort-selector"
        @input="saveStateAndLoadCameras()"
      />

      <ViewTypeSwitcher
        v-model="selectedView"
        class="mesh-cameras__view-type-switcher"
        @input="saveState()"
      />
    </div>

    <!-- Верхняя и нижняя строки действий занимают всю ширину и одинаково выравнивают сохранение. -->
    <div v-if="multiSelectableMode" class="mesh-cameras__selection-actions">
      <div v-if="allowSelectAll" class="mesh-cameras__selection-tools">
        <CamsButton
          size="l"
          :disabled="isLoadingCommon || isLoadingPage || isSelectingAll || isSavingSelection || !displayedPageCount"
          @click="selectAllCameras"
        >
          {{ $t('selectAllCameras') }}
        </CamsButton>
        <CamsButton
          size="l"
          :disabled="isLoadingCommon || isLoadingPage || isSelectingAll || isSavingSelection || !selectedCameraNumbers.length"
          @click="clearCameraSelection"
        >
          {{ $t('clearCameraSelection') }}
        </CamsButton>
      </div>
      <CamsButton
        class="mesh-cameras__selection-save"
        priority="primary"
        size="l"
        :disabled="isSelectingAll || isSavingSelection || isLoadingCommon || isLoadingPage || hasCameraLoadError"
        @click="saveSelectedCameras"
      >
        {{ $t('saveSelectedCameras') }}
      </CamsButton>
    </div>

    <SpinnerLoading v-if="isLoadingCommon" class="loader_center" color="blue" />
    <div v-else-if="hasCameraLoadError" role="alert">
      <p>{{ $t('loadAlarmCamerasError') }}</p>
      <CamsButton :disabled="isLoadingPage" @click="loadCameras">
        {{ $t('retry') }}
      </CamsButton>
    </div>
    <template v-else>
      <paginate
        v-if="displayedPageCount > 1"
        v-model="selectedPage"
        :click-handler="changePage"
        :hide-prev-next="true"
        :page-count="displayedPageCount"
        :page-range="5"
        active-class="smart-pagination__page_active"
        break-view-class="smart-pagination__page_collapse"
        container-class="smart-pagination"
        next-class="smart-pagination__page smart-pagination__next-prev"
        next-link-class="smart-pagination__page-link"
        next-text="<svg class='icon icon-arrow-left'><use xlink:href='#icon-arrow-left'></use></svg>"
        page-class="smart-pagination__page"
        page-link-class="smart-pagination__page-link"
        prev-class="smart-pagination__page smart-pagination__next-prev"
        prev-link-class="smart-pagination__page-link"
        prev-text="<svg class='icon icon-arrow-right'><use xlink:href='#icon-arrow-right'></use></svg>"
      />

      <SpinnerLoading v-if="isLoadingPage" class="loader_center" color="blue" />
      <div
        v-else-if="displayedCamerasInfo.length"
        class="mesh-cameras-grid"
        :class="[isTableView ? 'mesh-cameras-grid_table' : 'mesh-cameras-grid_tiles']"
      >
        <component
          :is="componentView"
          v-for="cameraInfo in displayedCamerasInfo"
          :key="cameraInfo.number"
          :camera-info="cameraInfo"
          :is-selected-camera="multiSelectableMode && selectedCameraNumbers.includes(cameraInfo.number)"
          :selected-camera-icon-only="selectedCameraIconOnly"
          :screenshot-sign="screenshotSign"
          :selectable-only-mode="true"
          class="mesh-cameras-grid__cell"
          @select-camera="selectCamera"
        />
      </div>
      <p v-else>
        {{ $t('noCamerasFound') }}
      </p>

      <paginate
        v-if="displayedPageCount > 1"
        v-model="selectedPage"
        :click-handler="changePage"
        :hide-prev-next="true"
        :page-count="displayedPageCount"
        :page-range="5"
        active-class="smart-pagination__page_active"
        break-view-class="smart-pagination__page_collapse"
        container-class="smart-pagination"
        next-class="smart-pagination__page smart-pagination__next-prev"
        next-link-class="smart-pagination__page-link"
        next-text="<svg class='icon icon-arrow-left'><use xlink:href='#icon-arrow-left'></use></svg>"
        page-class="smart-pagination__page"
        page-link-class="smart-pagination__page-link"
        prev-class="smart-pagination__page smart-pagination__next-prev"
        prev-link-class="smart-pagination__page-link"
        prev-text="<svg class='icon icon-arrow-right'><use xlink:href='#icon-arrow-right'></use></svg>"
      />
    </template>

    <div v-if="multiSelectableMode" class="mesh-cameras__selection-actions">
      <CamsButton
        class="mesh-cameras__selection-save"
        priority="primary"
        size="l"
        :disabled="isSelectingAll || isSavingSelection || isLoadingCommon || isLoadingPage || hasCameraLoadError"
        @click="saveSelectedCameras"
      >
        {{ $t('saveSelectedCameras') }}
      </CamsButton>
    </div>
  </div>
</template>

<script>
import {FRAMES, TABS} from "@/store/meshCameras/index.js";
import {PARAMS_SORT_CAMERAS, TOKEN_TTL, TYPES_VIEWS} from "@/utils/consts.js";
import {ACTION_LOAD_CAMERAS, ACTION_LOAD_CAMERAS_MY, ACTION_LOAD_CAMERAS_FAV, ACTION_SEARCH_CAMERAS} from "@/store/cameras/index.js";
import {whenToUpdateToken} from "@/utils/helpers.js";
import CamerasFinder from "@/components/meshCameras/CamerasFinder.vue";
import SelectSort from "@/components/meshCameras/SelectSort.vue";
import ViewTypeSwitcher from "@/components/meshCameras/ViewTypeSwitcher.vue";
import ViewTable from "@/components/meshCameras/ViewTable.vue";
import ViewTile from "@/components/meshCameras/ViewTile.vue";
import {meshFrameMixin} from "@/components/meshCameras/mixins.js";

/**
 * Компонент для представления списка камер на диалоговом окне для выбора конкретной камеры.
 */
export default {
  name: "MeshFrameDialog",
  mixins: [meshFrameMixin],
  components: {
    CamerasFinder,
    SelectSort,
    ViewTypeSwitcher,
  },
  props: {
    /**
     * Список выбранных камер для режима выбора нескольких камер. Если нужно выбирать по 1 камере передавать false.
     */
    initialSelectedCameraNumbers: {
      type: [Boolean, Array],
      default: false,
    },
    /**
     * Если передан массив, диалог показывает только камеры с указанными номерами.
     * Сохраняет вкладки и поиск; значение null отключает ограничение.
     */
    allowedCameraNumbers: {
      type: Array,
      default: null,
    },
    // Ограниченный список без вкладок: null — обычный режим; [] — нет доступных камер.
    availableCameraNumbers: {
      type: Array,
      default: null,
    },
    allowSelectAll: {
      type: Boolean,
      default: false,
    },
    selectedCameraIconOnly: {
      type: Boolean,
      default: false,
    },
    saveSelection: {
      type: Function,
      default: null,
    },
  },
  data() {
    return {
      currentFrame: FRAMES.DIALOG,
      selectedCameraNumbers: this.initialSelectedCameraNumbers
        ? this.initialSelectedCameraNumbers.filter(number => (
          (this.availableCameraNumbers === null || this.availableCameraNumbers.includes(number))
          && (this.allowedCameraNumbers === null || this.allowedCameraNumbers.map(String).includes(String(number)))
        ))
        : false,
      restrictedCamerasInfo: [],
      restrictedSearch: "",
      isSelectingAll: false,
      isSavingSelection: false,
      hasCameraLoadError: false,
    };
  },
  computed: {
    hasCameraRestriction() {
      return this.availableCameraNumbers !== null;
    },
    filteredRestrictedCamerasInfo() {
      const query = String(this.hasCameraRestriction
        ? this.restrictedSearch
        : this.selectedTab === TABS.SEARCH ? this.queryTabSearch : "").trim().toLowerCase();
      return this.restrictedCamerasInfo
        .filter(camera => this.hasCameraRestriction || this.selectedTab !== TABS.FAV || camera.inFavorites)
        .filter(camera => [camera.number, camera.title, camera.address]
          .some(value => String(value || "").toLowerCase().includes(query)));
    },
    displayedCamerasInfo() {
      if (!this.hasCameraRestriction && !this.hasCameraNumberRestriction) {
        return this.listCamerasInfo;
      }
      const pageSize = this.$store.state.meshCameras.pageSize;
      return this.filteredRestrictedCamerasInfo.slice((this.selectedPage - 1) * pageSize, this.selectedPage * pageSize);
    },
    displayedPageCount() {
      return this.hasCameraRestriction || this.hasCameraNumberRestriction
        ? Math.ceil(this.filteredRestrictedCamerasInfo.length / this.$store.state.meshCameras.pageSize)
        : this.pageCount;
    },
    /**
     * @return {Boolean} Флаг который определяет что можно выбирать несколько камер сразу а не по одной.
     */
    multiSelectableMode() {
      return Array.isArray(this.selectedCameraNumbers);
    },
    hasCameraNumberRestriction() {
      return Array.isArray(this.allowedCameraNumbers);
    },
    /**
     * Компонент для выбранного типа отображения.
     *
     * @return {Component}
     */
    componentView() {
      return _.get(
        {
          [TYPES_VIEWS.TABLE]: ViewTable,
          [TYPES_VIEWS.TILE]: ViewTile,
        },
        this.selectedView,
        ViewTile
      );
    },
    /**
     * @return {Boolean} true если выбрано отображение в виде таблицы.
     */
    isTableView() {
      return this.selectedView === TYPES_VIEWS.TABLE;
    },
  },
  methods: {
    recoverState() {
      meshFrameMixin.methods.recoverState.call(this);
      if (this.hasCameraRestriction) {
        this.selectedTab = TABS.MY;
        this.selectedPage = 1;
        this.selectedSort = PARAMS_SORT_CAMERAS.addr_asc;
      }
    },
    changePage() {
      if (!this.hasCameraRestriction) {
        this.saveStateAndLoadCameras();
      }
    },
    async loadCameras() {
      if (this._isDestroyed || this._isBeingDestroyed) {
        return;
      }
      if (!this.hasCameraRestriction && !this.hasCameraNumberRestriction) {
        return meshFrameMixin.methods.loadCameras.call(this);
      }
      clearTimeout(this.timeoutIdForReloadCameras);
      this.isLoadingPage = true;
      this.hasCameraLoadError = false;
      try {
        const cameraNumbers = this.hasCameraRestriction
          ? this.availableCameraNumbers
          : [...new Set(this.allowedCameraNumbers.map(String).filter(Boolean))];
        const camerasInfo = cameraNumbers.length
          ? await this.$store.dispatch(`cameras/${ACTION_LOAD_CAMERAS}`, {
            ...this.dataForLoadCameras,
            numbers: cameraNumbers,
          })
          : [];
        if (this._isDestroyed || this._isBeingDestroyed) {
          return;
        }
        this.restrictedCamerasInfo = camerasInfo;
        this.selectedPage = Math.max(1, Math.min(this.selectedPage, this.displayedPageCount));
      } catch {
        if (!this._isDestroyed && !this._isBeingDestroyed) {
          this.hasCameraLoadError = true;
        }
        return;
      } finally {
        if (!this._isDestroyed && !this._isBeingDestroyed) {
          this.isLoadingPage = false;
        }
      }
      if (this._isDestroyed || this._isBeingDestroyed) {
        return;
      }
      this.timeoutIdForReloadCameras = setTimeout(() => {
        this.loadCameras();
      }, whenToUpdateToken(TOKEN_TTL.DEFAULT));
    },
    async selectAllCameras() {
      if (!this.multiSelectableMode || this.isSelectingAll || this.isSavingSelection || this.isLoadingPage || this.hasCameraLoadError) {
        return;
      }
      if (this.hasCameraRestriction || this.hasCameraNumberRestriction) {
        this.selectedCameraNumbers = this.restrictedCamerasInfo.map(camera => camera.number);
        return;
      }
      this.isSelectingAll = true;
      try {
        const action = {
          [TABS.MY]: ACTION_LOAD_CAMERAS_MY,
          [TABS.FAV]: ACTION_LOAD_CAMERAS_FAV,
          [TABS.SEARCH]: ACTION_SEARCH_CAMERAS,
        }[this.selectedTab];
        const params = {...this.dataForLoadCameras}, cameraNumbers = [];
        for (let page = 1, pageCount = 1; page <= pageCount; page++) {
          const [cameras, totalPages] = await this.$store.dispatch(`cameras/${action}`, {...params, page});
          cameraNumbers.push(...cameras.map(camera => camera.number));
          pageCount = totalPages;
        }
        this.selectedCameraNumbers = [...new Set([...this.selectedCameraNumbers, ...cameraNumbers])];
      } catch {
        this.$camsdals.alert(this.$t('loadAlarmCamerasError'));
      } finally {
        this.isSelectingAll = false;
      }
    },
    clearCameraSelection() {
      if (!this.multiSelectableMode || this.isLoadingCommon || this.isLoadingPage || this.isSelectingAll || this.isSavingSelection) {
        return;
      }
      this.selectedCameraNumbers = [];
    },
    async saveSelectedCameras() {
      if (this.isSelectingAll || this.isSavingSelection || this.isLoadingCommon || this.isLoadingPage || this.hasCameraLoadError) {
        return;
      }
      if (!this.saveSelection) {
        this.$vuedals.close(this.selectedCameraNumbers);
        return;
      }
      this.isSavingSelection = true;
      try {
        await this.saveSelection([...this.selectedCameraNumbers]);
      } finally {
        if (!this._isDestroyed && !this._isBeingDestroyed) {
          this.isSavingSelection = false;
        }
      }
    },
    /**
     * Переключение таба со списком камер.
     * Необходимо вначале загрузить ранее сохраненное состояние, а после обновить его для сохранения открытого таба.
     *
     * @param {String} newTab
     */
    selectTab(newTab) {
      this.selectedTab = newTab;
      this.restoreStateForSelectedTab();
      this.saveState();
      this.isLoadingCommon = true;
      this.loadCameras().then(() => {
        this.isLoadingCommon = false;
      });
    },
    /**
     * Обработка случая когда необходимо показать все результаты работы от компонента поиска {@link CamerasFinder}.
     * Сохраняется поисковый запрос из компонента в хранилище и тем самым регулируется содержимое таба с поиском.
     */
    showAllSearchResults() {
      this.queryTabSearch = this.querySearchCameras;
      this.saveState();
      this.selectTab(TABS.SEARCH);
    },
    /**
     * Клик по конкретной камере из списка. Инициирует закрытие диалога и передачу номера камеры в родительский компонент.
     *
     * @param {Number} cameraNumber
     */
    selectCamera(cameraNumber) {
      if (this.isSavingSelection || this.isSelectingAll) {
        return;
      }
      if (this.hasCameraRestriction && !this.availableCameraNumbers.includes(cameraNumber)) {
        return;
      }
      if (this.hasCameraNumberRestriction
        && !this.allowedCameraNumbers.map(String).includes(String(cameraNumber))) {
        return;
      }

      if (!this.multiSelectableMode) {
        this.$vuedals.close(cameraNumber);
        return;
      }

      if (this.selectedCameraNumbers.includes(cameraNumber)) {
        this.selectedCameraNumbers.splice(this.selectedCameraNumbers.indexOf(cameraNumber), 1);
      } else {
        this.selectedCameraNumbers.push(cameraNumber);
      }
    },
  },
};
</script>

<style lang="scss">
@use "./scss/mesh-cameras.scss";
</style>
